import type { MeResponse, PublicConfig } from '@licita/contracts';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { IconMail } from './components/icons';
import { Footer } from './components/Shell';
import { Notice, ThemeToggle, Wordmark } from './components/ui';
import { api, ApiError, getEmpresaAtiva, semEmpresa, setEmpresaAtiva } from './lib/api';
import { empresaQuery, empresasQuery, type EmpresaResumo } from './lib/app';
import { authClient } from './lib/auth';
import { registrar as registrarServiceWorker } from './lib/push';
import { queryClient } from './lib/queries';
import { router, SessionContext } from './router';

type State =
  | { kind: 'carregando' }
  | { kind: 'sem-sessao' }
  | { kind: 'equipe'; me: MeResponse }
  | { kind: 'sem-empresa'; me: MeResponse }
  | { kind: 'ok'; me: MeResponse };

const CONTATO = 'contato@licita.dev';

/** Moldura das telas fora do painel (entrar, avisos): cabeçalho simples + rodapé de compliance. */
function Frame({ children, user, onSignOut }: { children: ReactNode; user?: string; onSignOut?: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex h-12 items-center justify-between gap-3 px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <Wordmark />
          <span className="app-chip app-chip-accent">cliente</span>
        </div>
        <div className="flex items-center gap-1.5">
          {user && <span className="hidden max-w-[220px] truncate text-[12px] text-mute sm:inline">{user}</span>}
          {onSignOut && (
            <button type="button" className="app-btn app-btn-ghost" onClick={onSignOut}>
              Sair
            </button>
          )}
          <ThemeToggle />
        </div>
      </header>
      <main id="conteudo" className="flex flex-1 justify-center px-4 py-10">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="app-card w-full max-w-[420px] self-start p-6">
      <h1 className="text-[18px] font-semibold tracking-[-0.2px]">{title}</h1>
      {children}
    </div>
  );
}

/** App do cliente (app.licita.dev). Acesso por link mágico no e-mail; só contas com papel `cliente`. */
export default function App() {
  const [state, setState] = useState<State>({ kind: 'carregando' });
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [staging, setStaging] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaResumo[]>([]);
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  useEffect(() => {
    void api<PublicConfig>('/config').then(
      (c) => setStaging(c.env !== 'production'),
      () => {},
    );
  }, []);

  const load = useCallback(async () => {
    let me: MeResponse;
    try {
      me = await api<MeResponse>('/me');
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) setMsg('Não foi possível falar com a API. Tente de novo em instantes.');
      return setState({ kind: 'sem-sessao' });
    }
    if (me.user.role !== 'cliente') return setState({ kind: 'equipe', me });
    // Empresas da pessoa: a ativa guardada vale se ainda estiver na lista; senão, a primeira.
    let lista: EmpresaResumo[] = [];
    try {
      lista = (await queryClient.fetchQuery(empresasQuery)).itens;
    } catch {}
    const guardada = getEmpresaAtiva();
    const ativa = lista.find((e) => e.id === guardada)?.id ?? lista[0]?.id ?? null;
    setEmpresaAtiva(ativa);
    setEmpresas(lista);
    setEmpresaId(ativa);
    // Confere o vínculo com a empresa já no boot (e aquece o cache do perfil).
    try {
      await queryClient.fetchQuery(empresaQuery);
    } catch (e) {
      if (semEmpresa(e)) return setState({ kind: 'sem-empresa', me });
    }
    setState({ kind: 'ok', me });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // PWA: registra o service worker (push) depois do boot e navega quando a pessoa toca numa notificação.
  useEffect(() => {
    if (state.kind !== 'ok' || !('serviceWorker' in navigator)) return;
    void registrarServiceWorker();
    const onMessage = (e: MessageEvent) => {
      const m = e.data as { tipo?: string; url?: string } | null;
      if (m?.tipo !== 'abrir' || !m.url) return;
      const u = new URL(m.url, location.origin);
      if (u.origin === location.origin) void router.navigate({ href: u.pathname + u.search + u.hash });
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [state.kind]);

  const magicLink = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    await authClient.signIn.magicLink({ email: email.trim().toLowerCase(), callbackURL: `${location.origin}/` }).catch(() => {});
    setBusy(false);
    setMsg('Se o e-mail tiver conta, o link chegou. Abra-o neste aparelho para entrar.');
  };

  /** Troca a empresa ativa: grava o id, zera o cache (as telas voltam ao esqueleto e refazem as consultas) e mantém a rota. */
  const trocarEmpresa = useCallback(async (id: string) => {
    if (id === getEmpresaAtiva()) return;
    setEmpresaAtiva(id);
    setEmpresaId(id);
    await queryClient.cancelQueries();
    await queryClient.resetQueries();
  }, []);

  const signOut = async () => {
    await authClient.signOut().catch(() => {});
    setEmpresaAtiva(null);
    queryClient.clear();
    setMsg(null);
    setState({ kind: 'sem-sessao' });
  };

  const notice = msg && (
    <div className="mt-4">
      <Notice>{msg}</Notice>
    </div>
  );

  if (state.kind === 'carregando')
    return (
      <Frame>
        <div
          className="h-40 w-full max-w-[420px] animate-pulse self-start rounded-lg bg-bg-3 motion-reduce:animate-none"
          aria-busy="true"
        />
      </Frame>
    );

  if (state.kind === 'sem-sessao')
    return (
      <Frame>
        <Card title="Entrar">
          <p className="mt-1.5 leading-relaxed text-fg-2">
            Informe o e-mail cadastrado pela equipe. Você recebe um link de acesso; não há senha.
          </p>
          <form onSubmit={magicLink} className="mt-5 flex flex-col gap-2">
            <label className="app-lbl" htmlFor="email">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              required
              className="app-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              autoFocus
            />
            <button type="submit" className="app-btn app-btn-primary mt-2 w-full" disabled={busy || !email}>
              <IconMail size={15} /> {busy ? 'enviando…' : 'Receber link de acesso'}
            </button>
          </form>
          {notice}
          <p className="mt-4 text-[12px] leading-relaxed text-mute">
            Ainda não é cliente? Fale com a equipe em{' '}
            <a href={`mailto:${CONTATO}`} className="text-accent hover:underline">
              {CONTATO}
            </a>
            .
          </p>
        </Card>
      </Frame>
    );

  if (state.kind === 'equipe')
    return (
      <Frame user={state.me.user.email} onSignOut={() => void signOut()}>
        <Card title="Área do cliente">
          <p className="mt-1.5 leading-relaxed text-fg-2">
            Esta é a área das empresas clientes. A equipe do licita.dev usa o backoffice em{' '}
            <a href="https://backoffice.licita.dev/" className="text-accent hover:underline">
              backoffice.licita.dev
            </a>
            .
          </p>
        </Card>
      </Frame>
    );

  if (state.kind === 'sem-empresa')
    return (
      <Frame user={state.me.user.email} onSignOut={() => void signOut()}>
        <Card title="Quase lá">
          <p className="mt-1.5 leading-relaxed text-fg-2">
            Sua conta existe, mas ainda não está ligada a uma empresa. A equipe faz esse vínculo; escreva para{' '}
            <a href={`mailto:${CONTATO}`} className="text-accent hover:underline">
              {CONTATO}
            </a>{' '}
            informando o CNPJ e o e-mail <span className="font-medium text-fg">{state.me.user.email}</span>.
          </p>
          <button type="button" className="app-btn app-btn-ghost mt-5" onClick={() => void load()}>
            Verificar de novo
          </button>
        </Card>
      </Frame>
    );

  return (
    <SessionContext.Provider
      value={{ me: state.me, staging, signOut: () => void signOut(), empresas, empresaId, trocarEmpresa: (id) => void trocarEmpresa(id) }}
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SessionContext.Provider>
  );
}
